import { Box, Grid, Paper, useTheme } from "@mui/material";
import { FC, useMemo } from "react";
import { Link, Outlet } from "react-router-dom";
import { Agent, Permissions } from "../../api/dtos";
import GridActions from "../../components/GridActions";
import SetLinks from "../../components/layout/SetLinks";
import { StyledButton } from "../../components/StyledButton";
import CollapsibleAppTable from "../../components/tables/CollapsibleAppTable";
import type { AppColumn } from "../../components/tables/AppTable.types";
import AgentsContext from "../../contexts/AgentsContext";
import { DefaultProjectCreateLink } from "../../contexts/layouts/LinkContext";
import useAgents from "../../hooks/data/useAgents";

function requiredAgentId(row: Agent): number {
    if (row.id === undefined || row.id === null) {
        throw new Error("Agent is missing its ID.");
    }

    return row.id;
}

const useAgentColumns = () => {
    const agentColumns = useMemo(() => {
        const columns: AppColumn<Agent>[] = [
            {
                id: "name",
                label: "Name",
                scalarType: "text",
                accessor: (row) => row.name,
                flex: 1,
            },
            {
                id: "actions",
                label: "",
                headerLabel: "",
                kind: "action",
                scalarType: "text",
                accessor: (row) => String(row.id ?? ""),
                width: 100,
                cell: ({ row }) => (
                    <GridActions
                        editPermission={Permissions.AdminAccess}
                        editUrl={`/admin/agents/edit/${requiredAgentId(row)}`}
                    />
                ),
            },
        ];

        return columns;
    }, []);

    return agentColumns;
};

const Agents: FC = () => {
    const columns = useAgentColumns();

    const theme = useTheme();

    const context = useAgents();

    const { data: agents, loading: agentsLoading } = context;

    const exportMetadata = useMemo(
        () => ({
            fileName: `Agents`,
            exportTitle: `Agents`,
            includeDateTime: true,
        }),
        [],
    );

    return (
        <AgentsContext.Provider value={context}>
            <Outlet />
            <SetLinks createProjectLink={DefaultProjectCreateLink} stageLinkEnabled={false} lotLinkEnabled={false} />
            <Grid container gap={theme.spacing(2)} justifyContent={"end"} marginBottom={theme.spacing(2)}>
                <StyledButton
                    variant="contained"
                    //@ts-expect-error
                    component={Link}
                    to={`/admin/agents/create`}
                    state={{
                        back: true,
                    }}
                >
                    Add Agent
                </StyledButton>
            </Grid>
            <Paper>
                <Box>
                    <CollapsibleAppTable
                        density="compact"
                        startCollapsed={false}
                        title="Agents"
                        loading={agentsLoading}
                        rows={agents ?? []}
                        columns={columns}
                        getRowId={requiredAgentId}
                        columnControls={{
                            menu: true,
                        }}
                        filterControls
                        export={exportMetadata}
                    />
                </Box>
            </Paper>
        </AgentsContext.Provider>
    );
};

export default Agents;
